import React from 'react'
import { Link } from 'react-router-dom'
import { QuestionMark, Settings } from '@mui/icons-material'
import { IconButton, Typography, useTheme } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { Trans } from 'react-i18next'
import { AppContextProps } from '../../hooks/useApp'
import { DashboardViewProps, HeatPumpStateWidgetConfig, TestViewProps, Widget, WidgetData } from './Widget'
import { SystemControl } from '../SystemControl'
import { HeatPumpPortControl } from '../ports/HeatPumpPortControl'
import { OptimizerControlPortControl } from '../ports/OptimizerControlPortControl'
import { OptimizerSchedule, PortValuesHeatPump } from '../../types/ports'
import { AutoManualLed } from '../../components/atoms/AutoManualLed'
import { ExpanderButton } from '../../components/atoms/ExpanderButton'
import { HeatPumpStatusView } from '../../components/HeatPumpStatusView'
import { HeatPumpPlanBar } from '../../components/charts/HeatPumpPlanBar'
import { Schedule } from '../../components/Schedule'
import { SocketMessage, isApplianceMessage } from '../../types/messaging'
import { heatPumpCop, heatPumpHasProblem, heatPumpModeKey, heatPumpStatusKey } from '../ports/heat-pump-status'

/**
 * Front page card of the Modbus heat pump appliance (port 0x23): live status
 * and the optimizer's plan, whose power levels 0..7 the plan bar shows as bar
 * heights. REASON_DEFAULT stretches mean "released to the heat pump's own control".
 */
export class HeatPumpStateWidget extends Widget<{}, HeatPumpStateWidgetConfig> {

  open: boolean

  constructor(data: WidgetData, control: SystemControl, app: AppContextProps) {
    super(data, control, app)
    this.open = false
  }

  get Icon() {
    return this.appliances.length ? this.appliances[0].Icon : QuestionMark
  }

  get width(): number {
    return 4
  }

  get height(): number {
    return 2
  }

  get textColor(): string {
    return 'statsText.main'
  }

  get bgColor(): string {
    return 'statsBackground.main'
  }

  /** The heat pump runs on its own control (Enion optimizer off). */
  get manual(): boolean {
    return this.appliance.getConfig('strategy') === 'MANUAL'
  }

  dashboardTitle(firstOfKind: boolean): string | undefined {
    if (firstOfKind) return this.app.t('Appliances')
    return undefined
  }

  heatPumpControl(): HeatPumpPortControl {
    return this.findOneControl('HEAT_PUMP') as HeatPumpPortControl
  }

  optimizerControl(): OptimizerControlPortControl {
    return this.findOneControl('OPTIMIZER_CONTROL') as OptimizerControlPortControl
  }

  draw(props: DashboardViewProps): React.ReactElement {
    const Icon = this.Icon
    const values = this.heatPumpControl().port.values as Partial<PortValuesHeatPump> | undefined
    const schedule: OptimizerSchedule = this.manual ? [] : (this.optimizerControl().port.values.schedule || [])
    const errorFlags = this.errorFlags()
    const isOffline = !values?.status
    const statusKey = heatPumpStatusKey(values)
    const modeKey = heatPumpModeKey(values)
    const problem = heatPumpHasProblem(values)
    const cop = heatPumpCop(values)
    const power = values?.power
    const flow = values?.t_flow
    const setpoint = values?.t_flow_setpoint

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const theme = useTheme()

    const summary: string[] = []
    if (typeof power === 'number') summary.push(`${(power / 1000).toFixed(1)} kW`)
    if (cop !== null) summary.push(`COP ${cop.toFixed(1)}`)
    if (typeof flow === 'number') {
      summary.push(typeof setpoint === 'number' ? `${flow.toFixed(0)} → ${setpoint.toFixed(0)} °C` : `${flow.toFixed(0)} °C`)
    }

    return (
      <Box sx={{
        backgroundColor: this.bgColor,
        boxShadow: theme.boxShadow.main,
        borderRadius: '1.25rem',
        height: '100%'
      }} data-cy={`box-${this.getName()}`}>
        <Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2.5rem auto 1rem 2rem',
            alignItems: 'center',
            paddingLeft: '1rem',
            minHeight: '2rem',
          }}>
            <Icon sx={{ color: isOffline ? 'gray' : this.textColor }}/>
            <Typography sx={{ color: isOffline ? 'gray' : this.textColor }}>
              {this.getName()}
              {problem && statusKey && ` (${this.app.t(statusKey)})`}
              {!problem && modeKey && ` · ${this.app.t(modeKey)}`}
            </Typography>
            <Box>
              <AutoManualLed disabled={isOffline} manual={this.manual} error={errorFlags.length > 0 || problem}/>
            </Box>
            <ExpanderButton open={this.open} disabled={props.upgrading} color={this.textColor} onToggle={() => {
              this.open = !this.open
              props.refresh()
            }}/>
          </Box>

          <Box sx={{ px: 4, pb: 2, color: this.textColor }}>
            {errorFlags.length > 0 &&
              <Box sx={{ color: 'error.light', fontSize: '85%' }}>{errorFlags.join(' ')}</Box>
            }
            {props.upgrading && <Box sx={{ color: 'primary.light' }}><Trans>Upgrading...</Trans></Box>}
            {!props.upgrading && summary.length > 0 &&
              <Box sx={{ fontSize: '90%', mb: 1 }} data-cy="text-Heat pump summary">{summary.join(' · ')}</Box>
            }
            {!props.upgrading && !this.manual && <HeatPumpPlanBar schedule={schedule}/>}
          </Box>

          {this.open && !props.upgrading &&
            <Box sx={{ mx: 4, mb: 2, color: this.textColor }}>
              <HeatPumpStatusView values={values}/>
              {!this.manual &&
                <Box sx={{ mt: 2 }}>
                  <Trans>Current plan</Trans>:
                  <Schedule sx={{ mt: 1 }} schedule={schedule} defaultText={this.app.t('HeatPump-Strategy-MANUAL')}/>
                </Box>
              }
              <Link to={`/appliances/${this.appliance.id}`}>
                <Stack direction="row-reverse" sx={{ mt: 2, mr: -1, cursor: 'pointer' }}>
                  <IconButton sx={{ transform: 'translateY(-25%)', color: this.textColor }}>
                    <Settings />
                  </IconButton>
                  <Typography sx={{ textDecoration: 'underline', color: this.textColor }}>
                    <Trans>Settings</Trans>
                  </Typography>
                </Stack>
              </Link>
            </Box>
          }
        </Stack>
      </Box>
    )
  }

  drawTestView(props: TestViewProps) {
    return <HeatPumpStatusView values={this.heatPumpControl().port.values}/>
  }

  needRefreshOnMessage(msg: SocketMessage): boolean {
    // Port values are live in the port objects and redrawn on every update;
    // only appliance config changes (strategy) need to be copied in.
    if (isApplianceMessage(msg)) {
      const i = this.appliances.map(a => a.id).indexOf(msg.id)
      if (i >= 0 && msg.config) {
        Object.assign(this.appliances[i].config, msg.config)
        return true
      }
    }
    return false
  }
}
